/**
 * Job Card Keyboard Navigation
 * Task 13.2: Implement keyboard navigation for job cards
 * Requirements: 11.2, 11.3
 * 
 * Features:
 * - Tab key moves focus in logical order
 * - Enter and Space key activation on job cards
 * - Arrow key navigation between cards
 * - Visual feedback for keyboard interactions
 */

(function() {
  'use strict';

  /**
   * Initialize keyboard navigation for job cards
   * Task 18.2: Support both desktop and mobile card classes
   */
  function initJobCardKeyboardNavigation() {
    const jobCards = document.querySelectorAll('.modern-job-card, .modern-job-card-mobile, .modern-job-card-v3');
    
    if (!jobCards.length) {
      return;
    }
    
    jobCards.forEach((card, index) => {
      // Task 13.2: Support Enter and Space key activation on job cards
      card.addEventListener('keydown', (e) => {
        // Enter or Space to activate card (navigate to job detail)
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          
          // Visual feedback - brief scale animation
          const originalTransform = card.style.transform;
          card.style.transform = 'scale(0.98)';
          
          setTimeout(() => {
            card.style.transform = originalTransform;
            
            // Trigger the card click to navigate to job detail
            card.click();
          }, 150);
        }
        
        // Task 13.2: Arrow key navigation between cards
        handleArrowKeyNavigation(e, card, jobCards);
      });
      
      // Ensure card is keyboard focusable (should already have tabindex="0" in HTML)
      if (!card.hasAttribute('tabindex')) {
        card.setAttribute('tabindex', '0');
      }
      
      // Ensure card has proper ARIA role (should already have role="button" in HTML)
      if (!card.hasAttribute('role')) {
        card.setAttribute('role', 'button');
      }
    });
  }

  /**
   * Handle arrow key navigation between job cards
   * @param {KeyboardEvent} e - The keyboard event
   * @param {HTMLElement} currentCard - The currently focused card
   * @param {NodeList} allCards - All job cards
   */
  function handleArrowKeyNavigation(e, currentCard, allCards) {
    const cardsArray = Array.from(allCards);
    const currentIndex = cardsArray.indexOf(currentCard);
    let nextCard = null;
    
    // Calculate grid columns based on screen width
    let columns = 2; // Desktop default (2-column grid)
    if (window.innerWidth <= 768) {
      columns = 1; // Mobile (1-column grid)
    }
    
    switch(e.key) {
      case 'ArrowRight':
        e.preventDefault();
        // Move to next card (right)
        if (currentIndex < cardsArray.length - 1) {
          nextCard = cardsArray[currentIndex + 1];
        }
        break;
        
      case 'ArrowLeft':
        e.preventDefault();
        // Move to previous card (left)
        if (currentIndex > 0) {
          nextCard = cardsArray[currentIndex - 1];
        }
        break;
        
      case 'ArrowDown':
        e.preventDefault();
        // Move down one row
        if (currentIndex + columns < cardsArray.length) {
          nextCard = cardsArray[currentIndex + columns];
        }
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        // Move up one row
        if (currentIndex - columns >= 0) {
          nextCard = cardsArray[currentIndex - columns];
        }
        break;
    }
    
    if (nextCard) {
      nextCard.focus();
      
      // Smooth scroll into view
      nextCard.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }

  /**
   * Initialize keyboard navigation for tags within job cards
   * Task 18.2: Support both desktop and mobile tag classes
   */
  function initTagKeyboardNavigation() {
    const tags = document.querySelectorAll('.job-tag, .job-tag-mobile, .job-tag-v3');
    
    tags.forEach(tag => {
      // Ensure tags are keyboard focusable
      if (!tag.hasAttribute('tabindex')) {
        tag.setAttribute('tabindex', '0');
      }
      
      // Handle Enter and Space key activation
      tag.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation(); // Prevent card activation
          
          // Visual feedback
          const originalTransform = tag.style.transform;
          tag.style.transform = 'scale(0.95)';
          
          setTimeout(() => {
            tag.style.transform = originalTransform;
            
            // Navigate to tag page
            const link = tag.querySelector('a') || tag;
            if (link.href) {
              window.location.href = link.href;
            }
          }, 100);
        }
      });
    });
  }

  /**
   * Initialize keyboard navigation for apply buttons
   * Task 18.2: Support both desktop and mobile button classes
   */
  function initApplyButtonKeyboardNavigation() {
    const applyButtons = document.querySelectorAll('.apply-btn, .apply-btn-mobile');
    
    applyButtons.forEach(button => {
      // Handle Enter and Space key activation
      button.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation(); // Prevent card activation
          
          // Visual feedback
          const originalTransform = button.style.transform;
          button.style.transform = 'scale(0.95)';
          
          setTimeout(() => {
            button.style.transform = originalTransform;
            
            // Trigger button click
            button.click();
          }, 100);
        }
      });
    });
  }

  /**
   * Initialize keyboard navigation for favorite buttons
   */
  function initFavoriteButtonKeyboardNavigation() {
    const favoriteButtons = document.querySelectorAll('.fav-job-button');
    
    favoriteButtons.forEach(button => {
      // Ensure buttons are keyboard focusable
      if (!button.hasAttribute('tabindex')) {
        button.setAttribute('tabindex', '0');
      }
      
      // Add ARIA label if not present
      if (!button.hasAttribute('aria-label')) {
        button.setAttribute('aria-label', 'Add to favorites');
      }
      
      // Handle Enter and Space key activation
      button.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation(); // Prevent card activation
          
          // Visual feedback - pulse animation
          button.style.transform = 'scale(1.2)';
          
          setTimeout(() => {
            button.style.transform = 'scale(1)';
            
            // Trigger button click
            button.click();
          }, 150);
        }
      });
    });
  }

  /**
   * Initialize keyboard navigation for location links
   * Task 18.2: Support both desktop and mobile location link classes
   */
  function initLocationLinkKeyboardNavigation() {
    const locationLinks = document.querySelectorAll('.location-text a, .location-link, .location-link-mobile, .company-link-v3');
    
    locationLinks.forEach(link => {
      // Handle Enter and Space key activation
      link.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation(); // Prevent card activation
          
          // Navigate to location page
          window.location.href = link.href;
        }
      });
    });
  }

  /**
   * Task 13.2: Ensure Tab key moves focus in logical order
   * This is handled by the browser's default behavior with proper HTML structure
   * and tabindex attributes. We just need to ensure all interactive elements
   * are in the correct DOM order.
   */
  function ensureLogicalTabOrder() {
    const jobCards = document.querySelectorAll('.modern-job-card, .modern-job-card-mobile, .modern-job-card-v3');
    
    jobCards.forEach(card => {
      // Verify tab order within each card:
      // 1. Card itself (for Enter/Space activation)
      // 2. Favorite button
      // 3. Location links
      // 4. Tags
      // 5. Apply button
      
      // The browser will handle tab order based on DOM order
      // We just ensure all interactive elements have proper tabindex
      const interactiveElements = card.querySelectorAll('a, button, [tabindex]');
      
      interactiveElements.forEach(element => {
        // Remove negative tabindex that would exclude from tab order
        if (element.getAttribute('tabindex') === '-1') {
          element.removeAttribute('tabindex');
        }
      });
    });
  }

  /**
   * Initialize all keyboard navigation features
   */
  function init() {
    initJobCardKeyboardNavigation();
    initTagKeyboardNavigation();
    initApplyButtonKeyboardNavigation();
    initFavoriteButtonKeyboardNavigation();
    initLocationLinkKeyboardNavigation();
    ensureLogicalTabOrder();
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on Turbo navigation (if using Turbo)
  document.addEventListener('turbo:load', init);
  
  // Re-initialize on Turbo frame load
  document.addEventListener('turbo:frame-load', init);

  // Export for testing or manual use
  window.JobCardKeyboardNavigation = {
    init,
    initJobCardKeyboardNavigation,
    initTagKeyboardNavigation,
    initApplyButtonKeyboardNavigation,
    initFavoriteButtonKeyboardNavigation,
    initLocationLinkKeyboardNavigation,
    ensureLogicalTabOrder
  };
})();
